import {
  type SaveDispatchGroupDto,
  type SaveDispatchGroupsGroupResultEntity,
  type ShipmentOrderDispatchGroupExEntity,
  type ShipmentOrdersListExEntity
} from '@/libs/api'
import { queryClient } from '@/libs/react-query/query-client'

import { type FetchVehicleDispatchBoardsResponse } from '../api/fetch-vehicle-dispatch-boards'
import {
  type NotDispatchedShipmentOrderQueryType,
  vehicleDispatchBoardsQueryKeys
} from '../query-keys'

import {
  invalidateNewBoardCache,
  invalidateNotDispatchedShipmentOrderCaches,
  newBoardQueryKey
} from './board-cache-invalidation'
import { compareByStartAtThenId, envelopeOf, overlaps } from './time-span'

type BoardData = FetchVehicleDispatchBoardsResponse['data']
type BoardDispatch = BoardData['dispatches'][number]
type BoardDetail = BoardDispatch['details'][number]
type BoardGroup = BoardDispatch['dispatchGroups'][number]
type BoardShipmentOrder = BoardDetail['shipmentOrders'][number]

/**
 * 単独化（宣言 id: null）で描く仮の束ね id。負値で本物の id と衝突させない。
 * 応答後の再取得でサーバが振った id に置き換わる。
 */
let nextTemporaryGroupId = -1
const takeTemporaryGroupId = () => nextTemporaryGroupId--

/** 新規配車（宣言 id: null + shipmentOrderId）で描く仮の明細 id。扱いは束ねの仮 id と同じ */
let nextTemporaryDetailId = -1
const takeTemporaryDetailId = () => nextTemporaryDetailId--

/** 未配車一覧（配送依頼パネル）のキャッシュ形。ドロップ元の一覧に必ず実体がある */
type NotDispatchedOrdersCache = { data: ShipmentOrdersListExEntity[] }

/** 通常一覧・全件一覧のキー名（notDispatchedShipmentOrderQueryTypes の部分集合） */
const notDispatchedListKeys = [
  'notDispatchedShipmentOrdersList',
  'allNotDispatchedShipmentOrdersList'
] as const satisfies readonly NotDispatchedShipmentOrderQueryType[]

/**
 * 束カード単位の未配車一覧（配送依頼パネルの描画元）のキャッシュ形。
 * カード 1 枚 = ShipmentOrderDispatchGroupExEntity（単体依頼は orders 1 件）
 */
type NotDispatchedGroupsCache = { data: ShipmentOrderDispatchGroupExEntity[] }

const notDispatchedGroupsQueryKey = [
  ...vehicleDispatchBoardsQueryKeys.all,
  'notDispatchedShipmentOrderDispatchGroups' satisfies NotDispatchedShipmentOrderQueryType
]

/**
 * 新規配車の宣言（id: null + shipmentOrderId）に載せる配送依頼を、
 * 未配車一覧キャッシュ（通常・全件の両方）から引く。
 */
const findNotDispatchedShipmentOrder = (
  shipmentOrderId: number
): ShipmentOrdersListExEntity | undefined => {
  for (const listKey of notDispatchedListKeys) {
    const entries = queryClient.getQueriesData<NotDispatchedOrdersCache>({
      queryKey: [...vehicleDispatchBoardsQueryKeys.all, listKey]
    })
    for (const [, cached] of entries) {
      const order = cached?.data?.find((o) => o.id === shipmentOrderId)
      if (order) return order
    }
  }
  // 束カード単位の一覧（パネルの描画元）の配下からも引く
  const groupEntries = queryClient.getQueriesData<NotDispatchedGroupsCache>({
    queryKey: notDispatchedGroupsQueryKey
  })
  for (const [, cached] of groupEntries) {
    for (const card of cached?.data ?? []) {
      const order = card.orders.find((o) => o.id === shipmentOrderId)
      if (order) return order
    }
  }
  return undefined
}

/**
 * 配車した依頼のカードを、応答を待たずに未配車一覧（通常・全件）から消す（楽観更新）。
 *
 * 配車の楽観更新は板に矢羽根を描くが、一覧側のカードはキャッシュに残るため、
 * これを入れないと再取得が返るまで「板に矢羽根・一覧にカード」の二重表示になり、
 * ドロップした依頼が一覧に戻ったように見える。
 *
 * 消す前に進行中の一覧取得を捨てる。捨てないと、ドラッグ中に始まった取得の応答
 * （配車前なのでカードを含む）が、消した後のキャッシュを上書きしてカードが復活する。
 *
 * 合計重量・束ねカードの数値は再取得で収束させる。
 * 失敗時は runWithOptimisticRollback が未配車系も再取得して巻き戻す。
 */
export const removeOptimisticNotDispatchedOrders = async (
  shipmentOrderIds: number[]
) => {
  if (shipmentOrderIds.length === 0) return
  const removedIds = new Set(shipmentOrderIds)

  for (const listKey of notDispatchedListKeys) {
    const listQueryKey = [...vehicleDispatchBoardsQueryKeys.all, listKey]
    await queryClient.cancelQueries({ queryKey: listQueryKey })
    const entries = queryClient.getQueriesData<NotDispatchedOrdersCache>({
      queryKey: listQueryKey
    })
    for (const [queryKey, cached] of entries) {
      if (!cached?.data?.some((order) => removedIds.has(order.id))) continue
      queryClient.setQueryData<NotDispatchedOrdersCache>(queryKey, {
        ...cached,
        data: cached.data.filter((order) => !removedIds.has(order.id))
      })
    }
  }

  // パネルのカードは束カード単位の一覧から描かれるため、こちらからも消す。
  // 束の一部だけ配車したときはカードに残りの依頼を残し、空になったカードは消す
  await queryClient.cancelQueries({ queryKey: notDispatchedGroupsQueryKey })
  const groupEntries = queryClient.getQueriesData<NotDispatchedGroupsCache>({
    queryKey: notDispatchedGroupsQueryKey
  })
  for (const [queryKey, cached] of groupEntries) {
    const hasRemoved = cached?.data.some((card) =>
      card.orders.some((order) => removedIds.has(order.id))
    )
    if (!cached || !hasRemoved) continue
    queryClient.setQueryData<NotDispatchedGroupsCache>(queryKey, {
      ...cached,
      data: cached.data
        .map((card) => ({
          ...card,
          orders: card.orders.filter((order) => !removedIds.has(order.id))
        }))
        .filter((card) => card.orders.length > 0)
    })
  }
}

/**
 * 未配車一覧の配送依頼（entity 形）を、板 API が返す素の形へ写す。
 * 板キャッシュは formatter（formatShipmentOrder）の入力形で持つ必要があるため、
 * formatter が読む項目を entity から埋める。集計済みの明細（items）は
 * orderToDetails の形へ戻す。customerContract（nested）は entity に無いが、
 * formatter が customerContractName へフォールバックするので flat 側だけ渡す。
 */
const toBoardShipmentOrder = (
  order: ShipmentOrdersListExEntity
): BoardShipmentOrder => ({
  id: order.id,
  deliveryStatus: order.deliveryStatus,
  income: order.income,
  incomeTaxCategoryId: order.incomeTaxCategoryId,
  incomeIsTaxInclusive: order.incomeIsTaxInclusive,
  incomeRoundType: order.incomeRoundType,
  incomeRecordingDate: order.incomeRecordingDate,
  isManual: order.isManual,
  orderNo: order.orderNo,
  note: order.note,
  bundle:
    order.bundleId != null
      ? { id: order.bundleId, label: order.bundleLabel }
      : null,
  customKey1: order.customKey1,
  customKey2: order.customKey2,
  customKey3: order.customKey3,
  customKey4: order.customKey4,
  customKey5: order.customKey5,
  waypoints: order.waypoints,
  originNote: order.originNote,
  destNote: order.destNote,
  memoContent: order.memoContent,
  regularGroupId: order.regularGroupId,
  quantity: order.quantity,
  updatedAt: order.updatedAt,
  truckModelId: order.truckModelId,
  truckModel: order.truckModel,
  partner: order.partner,
  customerContractId: order.customerContractId,
  customerContractName: order.customerContractName,
  originHistoryId: order.originHistoryId,
  groupId: order.groupId,
  group: order.group,
  orderToDetails: order.items.map((item) => ({
    detail: {
      id: item.detailId,
      code: item.code ?? '',
      itemCode: item.itemCode,
      itemName: item.itemName,
      weight: item.weight,
      quantity: item.quantity,
      packageCount: item.packageCount,
      packagedLoadQuantity: item.packagedLoadQuantity
    },
    packageCount: item.packageCount
  })),
  departureTimeRestrictionStart: order.departureTimeRestrictionStart,
  departureTimeRestrictionEnd: order.departureTimeRestrictionEnd,
  arrivalTimeRestrictionStart: order.arrivalTimeRestrictionStart,
  arrivalTimeRestrictionEnd: order.arrivalTimeRestrictionEnd,
  isLoadingWorkOutsourced: order.isLoadingWorkOutsourced,
  loadingWorkMinutes: order.loadingWorkMinutes,
  isUnloadingWorkOutsourced: order.isUnloadingWorkOutsourced,
  unloadingWorkMinutes: order.unloadingWorkMinutes,
  // 荷役作業の備考 (#4893)。上の4項目と同じ経路で引き継ぐ
  loadingWorkNote: order.loadingWorkNote,
  unloadingWorkNote: order.unloadingWorkNote,
  additionalCharges: order.additionalCharges
})

/** 束ねの区間は当てながら書き換えるため、明細配列・束ねの実体まで複製する */
const cloneDispatches = (data: BoardData): BoardDispatch[] =>
  data.dispatches.map((dispatch) => ({
    ...dispatch,
    details: [...dispatch.details],
    dispatchGroups: dispatch.dispatchGroups.map((group) => ({ ...group }))
  }))

const indexGroups = (dispatches: BoardDispatch[]) => {
  const groupById = new Map<number, BoardGroup>()
  for (const dispatch of dispatches) {
    for (const group of dispatch.dispatchGroups) groupById.set(group.id, group)
  }
  return groupById
}

/**
 * 配下が変わった束ねの区間を引き直し、その束ねを指す明細側の参照も差し替える。
 */
const rebuildGroupSpans = (
  dispatches: BoardDispatch[],
  groupById: Map<number, BoardGroup>,
  affectedGroupIds: Set<number>
) => {
  const detailsByGroupId = new Map<number, BoardDetail[]>()
  for (const dispatch of dispatches) {
    for (const detail of dispatch.details) {
      const details = detailsByGroupId.get(detail.dispatchGroupId)
      if (details) details.push(detail)
      else detailsByGroupId.set(detail.dispatchGroupId, [detail])
    }
  }

  // 親矢羽根の区間 = 配下明細の最小開始〜最大終了。配下が空になった束ねは
  // 矢羽根が描かれないため触らない
  for (const groupId of affectedGroupIds) {
    const group = groupById.get(groupId)
    const envelope = envelopeOf(detailsByGroupId.get(groupId) ?? [])
    if (!group || !envelope) continue
    group.startAt = envelope.startAt
    group.endAt = envelope.endAt
  }

  // 時モードの束ね一覧は明細に付いてくる dispatchGroup を優先して拾うため、
  // 区間を引き直した束ねを指す明細側も差し替える。
  // 並びは板 API と同じ開始時刻昇順に揃える（行内の段組みが順序を前提にしている）。
  // 引き直した束ねに触れない Dispatch は表示が変わらないため配列を作り直さない
  for (const dispatch of dispatches) {
    if (
      !dispatch.details.some((detail) =>
        affectedGroupIds.has(detail.dispatchGroupId)
      )
    ) {
      continue
    }
    dispatch.details = dispatch.details
      .map((detail) =>
        affectedGroupIds.has(detail.dispatchGroupId)
          ? {
              ...detail,
              dispatchGroup: groupById.get(detail.dispatchGroupId) ?? null
            }
          : detail
      )
      .sort(compareByStartAtThenId)
  }
}

/** 同じ行（planSlot、無い場合は truck）に載っている Dispatch か */
const isSameBoardRow = (a: BoardDispatch, b: BoardDispatch) => {
  if (a.id === b.id) return true
  if (a.planSlotId != null || b.planSlotId != null) {
    return a.planSlotId === b.planSlotId
  }
  return a.truckId != null && a.truckId === b.truckId
}

/**
 * 板から取り除く空き時間枠(FREE_TIME)を決めるための着地。
 * sourceGroupId は動かす明細の移動前の束ね。BE は重複判定でこの束ねを除外する
 * （checkAndDeleteOverlappingDetailsForIntervals の excludeDispatchGroupId）ため、
 * 同じ束ねの中の空き時間枠は消えない
 */
type FreeTimeReplacementLanding = {
  dispatch: BoardDispatch
  startAt: string
  endAt: string
  sourceGroupId?: number
}

/**
 * 着地区間に重なる空き時間枠(FREE_TIME)を板から取り除く。
 *
 * 空き時間枠は矢羽根を重ねると BE が削除する（枠が矢羽根に入れ替わる）。楽観更新でも
 * 同じ形にしないと、再取得までは「置き換わったはずの枠が矢羽根の下に残っている」板が
 * 描かれる。着地区間は空き時間枠へ吸着済み（snapToFreeTimeFrame）なので、ここで
 * 取り除く枠は利用者が狙って置き換えた枠そのものになる。
 *
 * 対象は BE が実際に削除する枠だけに合わせる。多く消すと、保存中だけ枠が消えて
 * 再取得で戻る「ちらつき」になる（呼び出し側で着地を絞る条件も参照）。
 *
 * 行は日を跨いで描かれるため、枠が着地先とは別日の Dispatch に載っていることがある。
 * 同じ行に属する Dispatch 全件を対象にする。
 */
const removeReplacedFreeTimeDetails = (
  dispatches: BoardDispatch[],
  landings: FreeTimeReplacementLanding[],
  affectedGroupIds: Set<number>
) => {
  if (landings.length === 0) return

  const dispatchesWithRemoval: BoardDispatch[] = []

  for (const dispatch of dispatches) {
    const intervals = landings.filter((landing) =>
      isSameBoardRow(landing.dispatch, dispatch)
    )
    if (intervals.length === 0) continue

    // 枠は配送と別の配列（frames[]）で来る
    const remaining = (dispatch.frames ?? []).filter((frame) => {
      const isReplaced =
        frame.frameType === 'FREE_TIME' &&
        intervals.some(
          (interval) =>
            // 移動前と同じ複数矢羽の枠は BE 側でも判定から外れるので消さない
            interval.sourceGroupId !== frame.dispatchGroupId &&
            overlaps(
              frame.startAt,
              frame.endAt,
              interval.startAt,
              interval.endAt
            )
        )
      if (isReplaced) affectedGroupIds.add(frame.dispatchGroupId)
      return !isReplaced
    })
    if (remaining.length === (dispatch.frames ?? []).length) continue

    dispatch.frames = remaining
    dispatchesWithRemoval.push(dispatch)
  }

  if (dispatchesWithRemoval.length === 0) return

  // 配下が空になった複数矢羽は板から落とす（BE も配送も枠も残らなければ削除する）。
  // 行は日を跨ぐため配下は複数の配車に散る。全件の削除を終えてから生存判定を 1 回引く
  const groupIdsInUse = new Set([
    ...dispatches.flatMap((d) =>
      d.details.map((detail) => detail.dispatchGroupId)
    ),
    ...dispatches.flatMap((d) =>
      (d.frames ?? []).map((frame) => frame.dispatchGroupId)
    )
  ])
  for (const dispatch of dispatchesWithRemoval) {
    dispatch.dispatchGroups = dispatch.dispatchGroups.filter((group) =>
      groupIdsInUse.has(group.id)
    )
  }
}

/**
 * 宣言（PUT /dispatch-groups の body）を板データへ当てる。
 * 宣言先 Dispatch がこのデータに無い場合は null を返して当てない。
 */
const patchBoardData = (
  data: BoardData,
  groups: SaveDispatchGroupDto[]
): BoardData | null => {
  const dispatches = cloneDispatches(data)
  const groupById = indexGroups(dispatches)

  // 宣言明細の現在地を引くための索引。明細を動かしたら追随させて整合を保つ
  const dispatchByDetailId = new Map<number, BoardDispatch>()
  for (const dispatch of dispatches) {
    for (const detail of dispatch.details) {
      dispatchByDetailId.set(detail.id, dispatch)
    }
  }

  // 区間を引き直す束ね。移動元（配下が減る側）と移動先の両方が対象
  const affectedGroupIds = new Set<number>()

  // 宣言明細の着地区間。重なった空き時間枠の置き換えを描くために使う。
  // 時刻が変わらない宣言（束ねの全件宣言・属性だけの編集）は着地に数えない —
  // BE は枠を消さないので、消すと保存中だけ枠が欠けて再取得で戻るちらつきになる
  const landings: FreeTimeReplacementLanding[] = []

  for (const declaredGroup of groups) {
    // 明細を宣言しない group は「楽観ロックだけ掛ける宣言」なので何も動かない
    const declaredDetails = (declaredGroup.details ?? []).filter(
      (detail) => detail.id != null || detail.shipmentOrderId != null
    )
    if (declaredDetails.length === 0) continue

    const target = dispatches.find((d) => d.id === declaredGroup.dispatchId)
    // 配車レコード未作成の行（負値 dispatchId）は行を捏造できないので諦める
    if (!target) return null

    const groupId = declaredGroup.id ?? takeTemporaryGroupId()
    let group = groupById.get(groupId)
    if (!group) {
      group = {
        id: groupId,
        startAt: '',
        endAt: '',
        expense: null,
        phoneNumber: declaredGroup.phoneNumber ?? null,
        driverName: null,
        driverUser: null
      }
      groupById.set(groupId, group)
      target.dispatchGroups.push(group)
    }
    affectedGroupIds.add(groupId)

    for (const declaredDetail of declaredDetails) {
      // 新規明細（未配車の配送依頼の配車）は矢羽根を捏造して描く
      if (declaredDetail.id == null) {
        const order = findNotDispatchedShipmentOrder(
          declaredDetail.shipmentOrderId as number
        )
        // 依頼の実体が引けない・着地区間が無い宣言は矢羽根を描き切れない。
        // 中途半端に当てず、この板は再取得での収束に任せる
        if (
          !order ||
          declaredDetail.startAt == null ||
          declaredDetail.endAt == null
        ) {
          return null
        }
        const newDetail: BoardDetail = {
          id: takeTemporaryDetailId(),
          dispatchId: target.id,
          dispatchGroupId: groupId,
          startAt: declaredDetail.startAt,
          endAt: declaredDetail.endAt,
          frameType: 'DELIVERY',
          frameLabel: null,
          // 配送依頼枠は色設定 UI を持たないため既定色のまま（VOC_PJ-450 #3830）
          frameColor: '#FFFFFF',
          regularGroupId: null,
          expense: declaredDetail.expense ?? null,
          shipmentOrders: [toBoardShipmentOrder(order)],
          dispatchGroup: group,
          // 分割運行専用の上書き値のため新規配車では常に空
          // （発着地の表示は shipmentOrders 側の group から引かれる）
          originSite: null,
          destSite: null
        }
        target.details.push(newDetail)
        dispatchByDetailId.set(newDetail.id, target)
        landings.push({
          dispatch: target,
          startAt: newDetail.startAt,
          endAt: newDetail.endAt
        })
        continue
      }

      const source = dispatchByDetailId.get(declaredDetail.id)
      // 表示窓の外にある明細は当てない（再取得で整合する）
      if (!source) continue

      const current = source.details.find(
        (detail) => detail.id === declaredDetail.id
      ) as BoardDetail
      affectedGroupIds.add(current.dispatchGroupId)
      source.details = source.details.filter(
        (detail) => detail.id !== declaredDetail.id
      )
      const isTimeChanged =
        (declaredDetail.startAt != null &&
          declaredDetail.startAt !== current.startAt) ||
        (declaredDetail.endAt != null && declaredDetail.endAt !== current.endAt)
      const sourceGroupId = current.dispatchGroupId

      const moved = {
        ...current,
        // 明細の所属 Dispatch は宣言先に合わせる。宵積み・日跨ぎでの
        // 厳密な所属はサーバ側の解決結果を再取得で受け取る
        dispatchId: target.id,
        dispatchGroupId: groupId,
        dispatchGroup: group,
        startAt: declaredDetail.startAt ?? current.startAt,
        endAt: declaredDetail.endAt ?? current.endAt
      }
      target.details.push(moved)
      dispatchByDetailId.set(current.id, target)
      if (isTimeChanged) {
        landings.push({
          dispatch: target,
          startAt: moved.startAt,
          endAt: moved.endAt,
          sourceGroupId
        })
      }
    }
  }

  if (affectedGroupIds.size === 0) return null

  removeReplacedFreeTimeDetails(dispatches, landings, affectedGroupIds)

  rebuildGroupSpans(dispatches, groupById, affectedGroupIds)

  return { ...data, dispatches }
}

/**
 * 配送依頼に戻した明細を板データから取り除く。
 * 取り除く明細がこのデータに無い場合は null を返して当てない。
 */
const removeDetailsFromBoardData = (
  data: BoardData,
  detailIds: number[]
): BoardData | null => {
  const removedIds = new Set(detailIds)
  // 明細が減った束ねは区間を引き直す（束ねの一部だけ戻すと親矢羽根が縮む）
  const affectedGroupIds = new Set<number>()

  const dispatches: BoardDispatch[] = data.dispatches.map((dispatch) => ({
    ...dispatch,
    details: dispatch.details.filter((detail) => {
      if (!removedIds.has(detail.id)) return true
      affectedGroupIds.add(detail.dispatchGroupId)
      return false
    }),
    dispatchGroups: dispatch.dispatchGroups.map((group) => ({ ...group }))
  }))

  if (affectedGroupIds.size === 0) return null

  rebuildGroupSpans(dispatches, indexGroups(dispatches), affectedGroupIds)

  return { ...data, dispatches }
}

/** 描かれている板キャッシュ全件に patch を当てる。null を返した板は触らない */
const patchBoardCaches = async (
  patch: (data: BoardData) => BoardData | null
) => {
  // 当てる前に進行中の板取得を捨てる。捨てないと、操作前に始まった取得の応答
  // （操作前の板）が、当てた後のキャッシュを上書きして操作結果が一瞬巻き戻る。
  // 捨てた分は保存後（失敗時は巻き戻し）の再取得で必ず取り直される
  await queryClient.cancelQueries({ queryKey: newBoardQueryKey })
  writeBoardCaches(patch)
}

/** 描かれている板キャッシュ全件に patch を当てる（進行中の取得は捨てない） */
const writeBoardCaches = (patch: (data: BoardData) => BoardData | null) => {
  const entries =
    queryClient.getQueriesData<FetchVehicleDispatchBoardsResponse>({
      queryKey: newBoardQueryKey
    })

  for (const [queryKey, cached] of entries) {
    if (!cached?.data) continue
    const patched = patch(cached.data)
    if (!patched) continue
    queryClient.setQueryData<FetchVehicleDispatchBoardsResponse>(queryKey, {
      ...cached,
      data: patched
    })
  }
}

/**
 * PUT /dispatch-groups へ送る宣言を、応答を待たずに板キャッシュへ当てる（楽観更新）。
 *
 * 宣言は保存後の姿そのものなので、そのまま当てればドロップ結果をその場で描ける。
 * これを入れないと、ドロップ直後に再取得が返るまで保存前の状態が描かれ、
 * ドラッグ中の予測表示 → 保存前の状態 → 保存結果と 2 回変化して見える。
 *
 * 新規明細の宣言（id: null + shipmentOrderId。未配車の配送依頼の配車）は、
 * 依頼の実体を未配車一覧キャッシュから引いて矢羽根を捏造して描く。
 *
 * 当てるのは見た目に要る項目（所属束ね・時刻・束ねの区間・依頼の内容）だけ。
 * 新規作成される明細・束ねの id はサーバ確定値を再取得で受け取る。
 * 失敗時は当てた API 関数が板を再取得して巻き戻す。
 */
export const applyOptimisticGroupSchedule = (
  groups: SaveDispatchGroupDto[]
) => {
  return patchBoardCaches((data) => patchBoardData(data, groups))
}

type SavedGroup = SaveDispatchGroupsGroupResultEntity
type SavedDetail = SavedGroup['details'][number]
type SavedFrame = SavedGroup['frames'][number]
type BoardFrame = NonNullable<BoardDispatch['frames']>[number]

/** 仮 id（負値）か。楽観更新が採番した id は応答の実 id へ置き換える対象 */
const isTemporaryId = (id: number) => id < 0

/**
 * 応答の束ねから、板に無い束ねを描くための最小形を組む。ドライバー等の属性は応答に
 * 無いため空で置き、再取得で埋める（描画に要るのは id と区間）
 */
const toBoardGroup = (saved: SavedGroup): BoardGroup => ({
  id: saved.id,
  startAt: saved.startAt,
  endAt: saved.endAt,
  updatedAt: saved.updatedAt,
  expense: null,
  phoneNumber: null,
  driverName: null,
  driverUser: null
})

/**
 * PUT /dispatch-groups の応答（保存後の束ね・明細・枠）を板データへ当てる。
 * 当てる対象が無ければ null を返して触らない。
 */
const applySavedGroupsToBoardData = (
  data: BoardData,
  savedGroups: SavedGroup[]
): BoardData | null => {
  const savedGroupById = new Map(savedGroups.map((g) => [g.id, g]))
  const savedDetailById = new Map<number, SavedDetail>(
    savedGroups.flatMap((g) => g.details.map((d) => [d.id, d]))
  )
  // 新規配車の明細は仮 id で描かれているため、載せた配送依頼で応答と対応付ける
  const savedDetailByOrderId = new Map<number, SavedDetail>(
    savedGroups.flatMap((g) =>
      g.details.flatMap((d) => (d.shipmentOrders ?? []).map((o) => [o.id, d]))
    )
  )
  const savedFrameById = new Map<number, SavedFrame>(
    savedGroups.flatMap((g) => (g.frames ?? []).map((f) => [f.id, f]))
  )

  let touched = false
  const dispatches = cloneDispatches(data)
  for (const dispatch of dispatches) {
    dispatch.frames = [...(dispatch.frames ?? [])]
  }

  // 仮 id の束ね → 応答の実 id。仮 id の明細が応答の明細に対応付いたときに確定する
  const resolvedGroupIds = new Map<number, number>()

  // 1. 明細: 楽観ロック値を当て、仮 id を実 id に置き換える
  for (const dispatch of dispatches) {
    dispatch.details = dispatch.details.map((detail) => {
      const saved =
        savedDetailById.get(detail.id) ??
        (isTemporaryId(detail.id)
          ? savedDetailByOrderId.get(detail.shipmentOrders[0]?.id as number)
          : undefined)
      if (!saved) return detail
      touched = true
      if (isTemporaryId(detail.dispatchGroupId)) {
        resolvedGroupIds.set(detail.dispatchGroupId, saved.dispatchGroupId)
      }
      return {
        ...detail,
        id: saved.id,
        updatedAt: saved.updatedAt,
        dispatchGroupId: saved.dispatchGroupId
      }
    })
  }

  // 2. 枠: 枠の移動は楽観更新を持たないため、所属・区間も応答で確定させる。
  //    別の Dispatch へ移った枠は移動先の frames[] へ載せ替える（移動先が窓外なら板から外す）
  const relocated: { frame: BoardFrame; sourceGroupId: number }[] = []
  for (const dispatch of dispatches) {
    dispatch.frames = (dispatch.frames ?? []).flatMap((frame) => {
      const saved = savedFrameById.get(frame.id)
      if (!saved) return [frame]
      touched = true
      const next: BoardFrame = {
        ...frame,
        updatedAt: saved.updatedAt,
        startAt: saved.startAt,
        endAt: saved.endAt,
        dispatchId: saved.dispatchId,
        dispatchGroupId: saved.dispatchGroupId
      }
      if (saved.dispatchId === dispatch.id) return [next]
      relocated.push({ frame: next, sourceGroupId: frame.dispatchGroupId })
      return []
    })
  }
  for (const { frame } of relocated) {
    const target = dispatches.find((d) => d.id === frame.dispatchId)
    if (!target) continue
    target.frames = [...(target.frames ?? []), frame].sort(
      compareByStartAtThenId
    )
    const saved = savedGroupById.get(frame.dispatchGroupId)
    if (saved && !target.dispatchGroups.some((g) => g.id === saved.id)) {
      target.dispatchGroups.push(toBoardGroup(saved))
    }
  }

  // 3. 束ね: 楽観ロック値と区間を当て、仮 id を実 id に置き換える。
  //    枠が抜けて空になり応答にも無い束ねは BE が消しているので板からも外す
  const emptiedGroupIds = new Set(relocated.map((r) => r.sourceGroupId))
  const memberGroupIds = new Set([
    ...dispatches.flatMap((d) => d.details.map((x) => x.dispatchGroupId)),
    ...dispatches.flatMap((d) => (d.frames ?? []).map((x) => x.dispatchGroupId))
  ])
  const patchGroup = <G extends BoardGroup | null | undefined>(group: G): G => {
    if (group == null) return group
    const id = resolvedGroupIds.get(group.id) ?? group.id
    const saved = savedGroupById.get(id)
    if (!saved) return group
    touched = true
    return {
      ...group,
      id,
      updatedAt: saved.updatedAt,
      startAt: saved.startAt,
      endAt: saved.endAt
    }
  }
  for (const dispatch of dispatches) {
    const seen = new Set<number>()
    dispatch.dispatchGroups = dispatch.dispatchGroups
      .filter((group) => {
        const isDeleted =
          emptiedGroupIds.has(group.id) &&
          !memberGroupIds.has(group.id) &&
          !savedGroupById.has(group.id)
        if (isDeleted) touched = true
        return !isDeleted
      })
      .map(patchGroup)
      .filter((group) => {
        if (seen.has(group.id)) return false
        seen.add(group.id)
        return true
      })
    // 明細・枠が抱える束ねの参照も同じ値へ揃える。所属が変わった（抱えている束ねの id と
    // dispatchGroupId が食い違う）ものは、載っている Dispatch の束ね一覧から引き直す
    const groupInDispatch = (groupId: number) =>
      dispatch.dispatchGroups.find((g) => g.id === groupId)
    const resolveMemberGroup = <
      G extends BoardGroup | null | undefined
    >(member: {
      dispatchGroupId: number
      dispatchGroup: G
    }): G => {
      const patched = patchGroup(member.dispatchGroup)
      if (patched != null && patched.id === member.dispatchGroupId) {
        return patched
      }
      return (groupInDispatch(member.dispatchGroupId) as G) ?? patched
    }
    dispatch.details = dispatch.details.map((detail) => {
      const dispatchGroup = resolveMemberGroup(detail)
      return dispatchGroup === detail.dispatchGroup
        ? detail
        : { ...detail, dispatchGroup }
    })
    dispatch.frames = (dispatch.frames ?? []).map((frame) => {
      const dispatchGroup = resolveMemberGroup(frame)
      return dispatchGroup === frame.dispatchGroup
        ? frame
        : { ...frame, dispatchGroup }
    })
  }

  return touched ? { ...data, dispatches } : null
}

/**
 * PUT /dispatch-groups の応答（保存後の束ね・明細・枠）を板キャッシュ全件へ当てる。
 *
 * 保存後の invalidate はアクティブ窓の再取得に任せるが、再取得が返る前に次のドロップが
 * 来ると、次の宣言は保存前の updatedAt で組まれて BE の層1照合（STALE_DISPATCH_STRUCTURE）
 * に必ず弾かれる。しかも次のドロップの楽観更新（patchBoardCaches）は進行中の再取得を
 * 捨てるので、新しい値がキャッシュに届く道が閉じる。応答は宣言した束ねの配下全件
 * （触っていない明細・枠も含む）の最新 updatedAt を持つので、それを再取得を待たずに
 * 当てておけば、連続操作でも直前の保存結果を土台に宣言できる。
 *
 * 当てる内容:
 * - 明細・枠・束ねの楽観ロック値（updatedAt）と束ねの区間
 * - 新規配車の楽観更新が振った仮 id（負値）→ 応答の実 id（明細は載せた配送依頼で対応付ける）。
 *   仮 id のままだと、再取得前にその矢羽根を動かす宣言が実在しない id で組まれる
 * - 枠の所属（Dispatch・束ね）と区間。枠の移動は楽観更新を持たないため、応答で確定させる
 *
 * 当てる前に進行中の板取得を捨てる。先行操作の invalidate で始まった取得は保存前の板を
 * 返すことがあり、捨てないと当てた最新値をその応答が後から上書きする。捨てた分は
 * 呼び出し元 mutation の onSuccess の invalidate で取り直される。
 *
 * id が板に無い（窓外）ものは触らず、再取得に任せる。
 */
export const applySavedDispatchGroupsToBoardCaches = async (
  savedGroups: SavedGroup[]
) => {
  if (savedGroups.length === 0) return
  await queryClient.cancelQueries({ queryKey: newBoardQueryKey })
  writeBoardCaches((data) => applySavedGroupsToBoardData(data, savedGroups))
}

/**
 * 配送依頼に戻す（＝配車キャンセル）矢羽根を、応答を待たずに板キャッシュから消す（楽観更新）。
 *
 * 戻す操作の結果は「その明細が板から消える」ことなので、宣言した明細を消せば足りる。
 * これを入れないと、配送依頼パネルへドロップした後も再取得が返るまで矢羽根が板に
 * 残り、掴んで離した位置に戻ったように見える。
 *
 * 束ねの一部だけ戻したときは、残った配下から親矢羽根の区間を引き直す。
 * 未配車一覧へ戻ってくる側はサーバ確定値を再取得で受け取る。
 * 失敗時は呼び出し側が板を再取得して巻き戻す。
 */
export const removeOptimisticBoardDetails = async (detailIds: number[]) => {
  if (detailIds.length === 0) return
  await patchBoardCaches((data) => removeDetailsFromBoardData(data, detailIds))
}

/**
 * 楽観更新を当てた後の保存リクエストを包む。失敗時は板と未配車系を再取得して
 * 楽観更新を巻き戻してから、元のエラーを投げ直す。
 * どちらのキャッシュもサーバ由来なので、再取得が巻き戻しそのものになる。
 *
 * 楽観更新を当てた関数（API 層）自身がこれで包むことで、呼び出し元の
 * mutation が増えても onError の巻き戻し漏れが起きない。
 */
export const runWithOptimisticRollback = async <T>(
  request: () => Promise<T>
): Promise<T> => {
  try {
    return await request()
  } catch (error) {
    // 巻き戻し（再取得）を待ってから投げ直す。待たずに throw すると、呼び出し元の
    // エラー処理や次の操作が「失敗した楽観データが残ったままの板」で始まってしまう。
    // 巻き戻し自体が失敗しても元のエラーを握り潰さないよう、ここでは握って捨てる
    // （握り潰すのは巻き戻しの失敗だけで、元の error は必ず投げ直す）
    await Promise.allSettled([
      invalidateNewBoardCache(),
      invalidateNotDispatchedShipmentOrderCaches()
    ])
    throw error
  }
}
